import BaseExportStrategy from './BaseExportStrategy';

/**
 * Estrategia de exportación para reportes de Info Cte y No Cte
 * Define las columnas y el formato específico para clasificación corriente/no corriente
 */
export default class InfoCteYNoCteExportStrategy extends BaseExportStrategy {
  constructor() {
    super('Info Cte y No Cte');
  }

  getTitle(context) {
    return 'INFO CTE Y NO CTE';
  }

  /**
   * Define las columnas del reporte de Info Cte y No Cte
   */
  getColumns(options = {}) {
    const columns = [];

    // 1. Legajo
    columns.push({
      key: 'legajo',
      header: 'Legajo',
      pdfWidth: 15,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.legajo || '-',

      formatSubtotal: (item, ctx) => 'SUBTOTAL',

      formatTotal: (items, ctx) => ''
    });

    // 2. Fideicomiso
    columns.push({
      key: 'fideicomiso',
      header: 'Fideicomiso',
      pdfWidth: 30,
      excelWidth: 30,
      align: 'left',

      format: (item, ctx) => item.fideicomiso || item.fideicomisoName || '-',

      formatSubtotal: (item, ctx) => `${item._cantidadRegistros || 0} registros`,

      formatTotal: (items, ctx) => 'TOTAL - RESUMEN'
    });

    // 3. Operatoria / Línea
    columns.push({
      key: 'operatoria_linea',
      header: 'Operatoria / Línea',
      pdfWidth: 38,
      excelWidth: 35,
      align: 'left',

      format: (item, ctx) => item.operatoria_linea || '-',

      formatSubtotal: (item, ctx) => {
        return (item._subtotales && item._subtotales.operatoria) || item.operatoria_linea || '-';
      },

      formatTotal: (items, ctx) => ''
    });

    // 4. Apellido y Nombre / Razón Social
    columns.push({
      key: 'nombre_razon_social',
      header: 'Apellido y Nombre / Razón Social',
      pdfWidth: 40,
      excelWidth: 40,
      align: 'left',

      format: (item, ctx) => {
        if (!item.nombre_razon_social) return '-';
        return item.nombre_razon_social.toUpperCase();
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 5. CUIT
    columns.push({
      key: 'cuit',
      header: 'CUIT',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => item.cuit || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 6. Localidad
    columns.push({
      key: 'localidad',
      header: 'Localidad',
      pdfWidth: 25,
      excelWidth: 20,
      align: 'left',

      format: (item, ctx) => item.localidad || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 7. Persona
    columns.push({
      key: 'persona',
      header: 'Persona',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.persona || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 8. Monto del Crédito
    columns.push({
      key: 'monto_otorgado',
      header: 'Monto del Crédito',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.monto_otorgado) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.monto_otorgado) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.monto_otorgado) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 9. Saldo Capital Total
    columns.push({
      key: 'saldo_capital_total',
      header: 'Saldo Capital Total',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.saldo_capital_total) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.saldo_capital_total) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.saldo_capital_total) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 10. Corriente
    columns.push({
      key: 'capital_corriente',
      header: 'Corriente',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.capital_corriente) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.capital_corriente) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.capital_corriente) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 11. No Corriente
    columns.push({
      key: 'capital_no_corriente',
      header: 'No Corriente',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.capital_no_corriente) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.capital_no_corriente) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.capital_no_corriente) || 0;
          return total + monto;
        }, 0);
      }
    });

    return columns;
  }

  /**
   * Info Cte y No Cte no usa desembolsos
   */
  getMaxDesembolsos(options) {
    return 0;
  }
}
